import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import WizardFooterSlot from 'common/components/wizardFooterSlot/WizardFooterSlot';
import TablePagination from 'common/components/tablePagination/TablePagination';
import SearchableSelectInput from 'common/components/searchableSelectInput/SearchableSelectInput';
import { SearchableSelectTitleSubTitleOption, OptionTitleSubTitle } from 'common/components/searchableSelectInput/optionsFormats/OptionTitleSubTitle';
import { Label } from 'common/components/texts/Texts';
import FamiliesService from 'api/families/FamiliesService';
import { ExcelSheetData, FamilyColumnMappingEntry, FAMILY_IMPORT_FIELDS } from '../utils/types';
import styles from './StepMapping.module.scss';

const ROWS_PER_PAGE = 100;

interface Props {
    excelData: ExcelSheetData;
    headerRowNumber: number;
    columnMappings: FamilyColumnMappingEntry[];
    templateFamilyId: string | null;
    templateSubFamilyId: string | null;
    onTemplateFamilyChange: (value: string | null) => void;
    onTemplateSubFamilyChange: (value: string | null) => void;
    onColumnMappingChange: (colIndex: number, field: string | null) => void;
    onHeaderRowChange: (row: number) => void;
}

const StepMapping: React.FC<Props> = ({
    excelData, headerRowNumber, columnMappings,
    templateFamilyId, templateSubFamilyId,
    onTemplateFamilyChange, onTemplateSubFamilyChange,
    onColumnMappingChange, onHeaderRowChange,
}) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(0);
    const subFamilySelectKey = useRef(0);

    const allRows = excelData.rows;
    const headerRowIdx = headerRowNumber - 1;

    // Column mapping exclusivity
    const usedFields = new Set(
        columnMappings.filter(m => m.mappedField).map(m => m.mappedField)
    );

    const getAvailableFields = (currentField: string | null) => {
        return FAMILY_IMPORT_FIELDS.filter(f => f.value === currentField || !usedFields.has(f.value));
    };

    // Find columns that have data in at least one row
    const visibleColIndices = useMemo(() => {
        const maxCols = Math.max(...allRows.map(r => r.length), 0);
        return Array.from({ length: maxCols }, (_, idx) => idx)
            .filter(colIdx => allRows.some(row => row[colIdx]?.trim()));
    }, [allRows]);

    // Filter out completely empty rows, but always keep the header row
    const visibleRows = useMemo(() => {
        return allRows
            .map((row, idx) => ({ row, rowIdx: idx }))
            .filter(({ row, rowIdx }) =>
                rowIdx === headerRowIdx || row.some(cell => cell?.trim())
            );
    }, [allRows, headerRowIdx]);

    const totalPages = Math.ceil(visibleRows.length / ROWS_PER_PAGE);

    // Paginated rows
    const paginatedRows = useMemo(() => {
        const start = currentPage * ROWS_PER_PAGE;
        return visibleRows.slice(start, start + ROWS_PER_PAGE);
    }, [visibleRows, currentPage]);

    const hasMappings = columnMappings.some(m => m.mappedField);

    const handleHeaderRowChange = (rowIdx: number) => {
        if (hasMappings) {
            if (!window.confirm(t('families.import_wizard.confirm_header_change'))) {
                return;
            }
        }
        onHeaderRowChange(rowIdx + 1);
        const visibleIndex = visibleRows.findIndex(r => r.rowIdx === rowIdx);
        const targetPage = visibleIndex >= 0 ? Math.floor(visibleIndex / ROWS_PER_PAGE) : 0;
        if (targetPage !== currentPage) {
            setCurrentPage(targetPage);
        }
    };

    // Column resize logic
    const defaultWidths = useMemo(() => {
        const widths: Record<string, number> = { radio: 36 };
        for (const colIdx of visibleColIndices) {
            widths[`col_${colIdx}`] = 200;
        }
        return widths;
    }, [visibleColIndices]);

    const [colWidths, setColWidths] = useState<Record<string, number>>({});
    const getWidth = (key: string) => colWidths[key] ?? defaultWidths[key] ?? 200;

    const handleResizeStart = useCallback((colKey: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = getWidth(colKey);

        const handleMouseMove = (ev: MouseEvent) => {
            const diff = ev.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff);
            setColWidths(prev => ({ ...prev, [colKey]: newWidth }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [colWidths, defaultWidths]);

    const resizeHandle = (colKey: string) => (
        <div
            className={styles.resizeHandle}
            onMouseDown={(e) => handleResizeStart(colKey, e)}
        />
    );

    // Template family loaders
    const loadFamilies = (
        inputValue: string,
        callback: (options: SearchableSelectTitleSubTitleOption[], hasMore: boolean) => void,
        _isStart: boolean,
        page: number
    ) => {
        FamiliesService.getList({
            isElement: true,
            isComponent: false,
            hasParentId: false,
            itemsPerPage: 20,
            page,
            name: inputValue
        }).then(result => {
            const options: SearchableSelectTitleSubTitleOption[] = result.items.map(f => ({
                value: f.id,
                label: f.name,
                subTitle: f.description ?? ''
            }));
            callback(options, result.currentPage < result.totalPages);
        });
    };

    const loadSubFamilies = (
        inputValue: string,
        callback: (options: SearchableSelectTitleSubTitleOption[], hasMore: boolean) => void,
        _isStart: boolean,
        page: number
    ) => {
        if (!templateFamilyId) {
            callback([], false);
            return;
        }
        FamiliesService.getList({
            isElement: true,
            isComponent: false,
            hasParentId: true,
            parentId: templateFamilyId,
            itemsPerPage: 20,
            page,
            name: inputValue
        }).then(result => {
            const options: SearchableSelectTitleSubTitleOption[] = result.items.map(f => ({
                value: f.id,
                label: f.name,
                subTitle: f.description ?? ''
            }));
            callback(options, result.currentPage < result.totalPages);
        });
    };

    const handleFamilyChange = (value: string | null | undefined) => {
        onTemplateFamilyChange(value ?? null);
        onTemplateSubFamilyChange(null);
        subFamilySelectKey.current += 1;
    };

    return (
        <div className={styles.container}>
            <div className={styles.description}>
                <p>{t('families.import_wizard.step_mapping_description')}</p>
            </div>

            <Row className="mb-3">
                <Col xs={6}>
                    <Label space>{t('families.import_wizard.template_family')}</Label>
                    <SearchableSelectInput<SearchableSelectTitleSubTitleOption>
                        value={templateFamilyId}
                        onChange={handleFamilyChange}
                        loadOptions={loadFamilies}
                        formatOptionLabel={OptionTitleSubTitle}
                    />
                </Col>
                <Col xs={6}>
                    <Label space>{t('families.import_wizard.template_sub_family')}</Label>
                    <SearchableSelectInput<SearchableSelectTitleSubTitleOption>
                        key={subFamilySelectKey.current}
                        value={templateSubFamilyId}
                        onChange={(value) => onTemplateSubFamilyChange(value ?? null)}
                        loadOptions={loadSubFamilies}
                        formatOptionLabel={OptionTitleSubTitle}
                        disabled={!templateFamilyId}
                    />
                </Col>
            </Row>

            <div className={styles.tableWrapper}>
                <table className={styles.table} style={{ width: getWidth('radio') + visibleColIndices.reduce((sum, idx) => sum + getWidth(`col_${idx}`), 0) }}>
                    <colgroup>
                        <col style={{ width: getWidth('radio') }} />
                        {visibleColIndices.map(colIdx => (
                            <col key={colIdx} style={{ width: getWidth(`col_${colIdx}`) }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr className={styles.mappingRow}>
                            <th className={styles.radioCol}></th>
                            {visibleColIndices.map(colIndex => {
                                const mapping = columnMappings.find(m => m.excelColumnIndex === colIndex);
                                const currentField = mapping?.mappedField ?? null;
                                const availableFields = getAvailableFields(currentField);

                                return (
                                    <th key={colIndex} className={styles.mappingCell}>
                                        <select
                                            value={currentField ?? ''}
                                            onChange={(e) => onColumnMappingChange(
                                                colIndex,
                                                e.target.value || null
                                            )}
                                            className={styles.mappingSelect}
                                        >
                                            <option value="">{t('families.import_wizard.field_not_mapped')}</option>
                                            {availableFields.map(f => (
                                                <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                        </select>
                                        {resizeHandle(`col_${colIndex}`)}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRows.map(({ row, rowIdx }) => {
                            const isHeader = rowIdx === headerRowIdx;
                            const isAboveHeader = rowIdx < headerRowIdx;

                            const rowClassName = [
                                styles.dataRow,
                                isHeader ? styles.headerSelectedRow : '',
                                isAboveHeader ? styles.aboveHeaderRow : '',
                            ].filter(Boolean).join(' ');

                            return (
                                <tr key={rowIdx} className={rowClassName}>
                                    <td className={styles.radioCol}>
                                        <input
                                            type="radio"
                                            name="headerRow"
                                            checked={isHeader}
                                            onChange={() => handleHeaderRowChange(rowIdx)}
                                            className={styles.radioInput}
                                        />
                                    </td>
                                    {visibleColIndices.map(colIndex => (
                                        <td
                                            key={colIndex}
                                            className={isHeader ? styles.headerCell : styles.dataCell}
                                            title={row[colIndex]}
                                        >
                                            {row[colIndex]}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <WizardFooterSlot>
                    <TablePagination
                        page={currentPage}
                        totalPages={totalPages}
                        onChange={setCurrentPage}
                        previousLabel={t('families.import_wizard.previous')}
                        nextLabel={t('families.import_wizard.next')}
                        info={t('families.import_wizard.page_info', { page: currentPage + 1, total: totalPages })}
                        firstTitle={t('families.import_wizard.first_page')}
                        lastTitle={t('families.import_wizard.last_page')}
                    />
                </WizardFooterSlot>
            )}
        </div>
    );
};

export default StepMapping;
